import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorText,
  HelpTip,
  type ActivityAlertSettings,
} from '@dataroom/shared';
import { useActivityAlerts, useSaveActivityAlerts } from '@/api/hooks';
import { intervalLabel } from '@/lib/engagement';

const SELECT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

/**
 * ADR-0056 — the signed-in admin's OWN "buyer opened a document" email for this
 * room: off until they turn it on, at most one email per chosen gap, listing the
 * facts (who opened what, pages and time). Each admin of the room (the main
 * admin and every co-admin) sets their own; saved as soon as it changes.
 */
export function ActivityAlertsCard({ roomId, readOnly }: { roomId: string; readOnly: boolean }) {
  const view = useActivityAlerts(roomId);
  const save = useSaveActivityAlerts(roomId);
  const data = view.data;
  const update = (patch: Partial<ActivityAlertSettings>) => {
    if (!data) return;
    save.mutate({ enabled: data.enabled, intervalMinutes: data.intervalMinutes, ...patch });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Buyer activity alerts <HelpTip id="admin.settings.alerts" className="ml-1" />
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          An email to you when buyers open documents in this room — who opened what, how many pages
          and for how long. Only you receive yours; the seller never sees buyer names.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {view.error ? (
          <ErrorText error={view.error} />
        ) : !data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <label className="flex w-fit items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={data.enabled}
                disabled={readOnly || save.isPending}
                onChange={(e) => update({ enabled: e.target.checked })}
              />
              Email me when a buyer opens a document
            </label>
            <div className="max-w-xs space-y-1.5">
              <label htmlFor="activity-alert-interval" className="text-sm font-medium">
                At most one email every
              </label>
              <select
                id="activity-alert-interval"
                className={SELECT_CLASS}
                value={data.intervalMinutes}
                disabled={readOnly || save.isPending || !data.enabled}
                onChange={(e) => update({ intervalMinutes: Number(e.target.value) })}
              >
                {data.intervalChoices.map((m) => (
                  <option key={m} value={m}>
                    {intervalLabel(m)}
                  </option>
                ))}
              </select>
            </div>
            {save.error ? <ErrorText error={save.error} /> : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
